import { useState, type CSSProperties } from "react";
import { ApiError } from "../api/client";
import { fetchGoogleAuthUrl } from "../api/auth";

const FLOATING_STICKERS: { x: string; y: string; rot: string; c: string; t: string; d: string }[] = [
  { x: "-180px", y: "-220px", rot: "-12deg", c: "#facc15", t: "COL", d: "0s" },
  { x: "120px", y: "-160px", rot: "8deg",   c: "#38bdf8", t: "ARG", d: "0.4s" },
  { x: "-160px", y: "60px",  rot: "16deg",  c: "#facc15", t: "BRA", d: "0.8s" },
  { x: "180px", y: "120px",  rot: "-10deg", c: "#0ea5e9", t: "URU", d: "1.2s" },
  { x: "0",     y: "200px",  rot: "4deg",   c: "#16a34a", t: "MEX", d: "1.6s" },
  { x: "-40px", y: "-40px",  rot: "-4deg",  c: "#7f1d1d", t: "POR", d: "2s" },
  { x: "200px", y: "-50px",  rot: "12deg",  c: "#1e40af", t: "USA", d: "0.6s" },
  { x: "-80px", y: "150px",  rot: "-8deg",  c: "#fbbf24", t: "ECU", d: "1.4s" },
];

export function AuthScreen() {
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      const { url, state } = await fetchGoogleAuthUrl();
      // Persistir state para validar al volver del callback (mitigación CSRF/OAuth login attack).
      sessionStorage.setItem("oauth_state", state);
      window.location.href = url;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "No se pudo iniciar Google login";
      setError(msg);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="auth-stage">
      <div className="auth-art">
        <div className="floating-stickers">
          {FLOATING_STICKERS.map((s, i) => (
            <div
              key={i}
              className="fs"
              style={{
                "--x": s.x,
                "--y": s.y,
                "--rot": s.rot,
                "--team-color": s.c,
                top: "50%",
                left: "50%",
                "--delay": s.d,
                animationDelay: s.d,
              } as CSSProperties}
            >
              {s.t}
            </div>
          ))}
        </div>
        <div className="auth-art-content">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <div className="brand-mark" style={{ width: 48, height: 48, fontSize: 22, background: "white", color: "var(--pitch-700)", borderRadius: 12, display: "grid", placeItems: "center", fontWeight: 800, fontFamily: "var(--font-display)" }}>P!</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em" }}>PaniniNilabs</div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.12)", padding: "6px 12px", borderRadius: 999, marginBottom: 12, fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>
            ⚽ Mundial 2026 · Canadá · México · USA
          </div>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 46, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.03em", margin: 0 }}>
            Completa el álbum
            <br />
            <span style={{ color: "var(--accent-amber)" }}>cambiando con tus repetidas.</span>
          </h2>
          <p style={{ fontSize: 17, opacity: 0.85, marginTop: 16, maxWidth: 420 }}>
            La plataforma de coleccionistas para intercambiar las 980 láminas del Mundial 2026.
            Match automático con quien tenga lo que te falta · chat directo · cero intermediarios.
          </p>
          <div style={{ display: "flex", gap: 24, marginTop: 32, flexWrap: "wrap" }}>
            <Stat value="980" label="Láminas" />
            <Stat value="48" label="Selecciones" />
            <Stat value="14" label="Coca-Cola" />
            <Stat value="100%" label="Gratis 🇨🇴" />
          </div>
          <div style={{ marginTop: 28, display: "flex", gap: 8, alignItems: "center", fontSize: 13, opacity: 0.75 }}>
            🇨🇴 🇦🇷 🇧🇷 🇲🇽 🇺🇾 🇨🇱 🇪🇨 🇵🇪 — abierto a toda Latinoamérica
          </div>
        </div>
      </div>

      <div className="auth-form-wrap">
        <div className="auth-form" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h1>Bienvenido</h1>
          <p className="sub">
            Entrá con tu cuenta de Google para ver tus matches del día.
          </p>

          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={startGoogle}
            disabled={googleLoading}
            style={{ width: "100%", justifyContent: "center", gap: 10, marginTop: 24 }}
          >
            <GoogleLogo />
            {googleLoading ? "Conectando…" : "Continuar con Google"}
          </button>

          {error && (
            <div className="err" style={{ marginTop: 16, padding: 10, background: "color-mix(in oklab, var(--accent-magenta) 12%, transparent)", borderRadius: 8 }}>
              {error}
            </div>
          )}

          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 24, lineHeight: 1.5 }}>
            Usamos Google solo para verificar tu correo. Nunca publicamos nada en tu cuenta.
            Si es la primera vez, te creamos el perfil automáticamente.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: 13, opacity: 0.7 }}>{label}</div>
    </div>
  );
}
